// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { getBlogPostBySlug } from "../../consts/blogPosts";
import { kanjiTextbookOrder } from "../../consts/kanjiTextbookOrder";
import Seo from "../Seo/SeoComponent";
import styles from "./blogPostPage.module.scss";

function formatDate(dateString) {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

const jlptLevels = [5, 4, 3, 2, 1];

// Surfaces kanji covered by a common teaching order (see kanjiTextbookOrder)
// first, in that order, ahead of everything else in the jōyō list.
const textbookPriority = new Map(kanjiTextbookOrder.map((symbol, index) => [symbol, index]));

function KanjiMeaningGrid({ items }) {
    const [activeLevel, setActiveLevel] = useState(5);
    const filteredByLevel = activeLevel === "all" ? items : items.filter((item) => item.jlptLevel === activeLevel);
    const filtered = [...filteredByLevel].sort((a, b) => {
        const priorityA = textbookPriority.has(a.symbol) ? textbookPriority.get(a.symbol) : Infinity;
        const priorityB = textbookPriority.has(b.symbol) ? textbookPriority.get(b.symbol) : Infinity;
        return priorityA - priorityB;
    });

    return (
        <div>
            <div className={styles.jlptFilterRow}>
                <button
                    type="button"
                    className={`${styles.jlptFilterButton} ${activeLevel === "all" ? styles.jlptFilterButtonActive : ""}`}
                    onClick={() => setActiveLevel("all")}
                >
                    All ({items.length})
                </button>
                {jlptLevels.map((level) => (
                    <button
                        key={level}
                        type="button"
                        className={`${styles.jlptFilterButton} ${activeLevel === level ? styles.jlptFilterButtonActive : ""}`}
                        onClick={() => setActiveLevel(level)}
                    >
                        N{level} ({items.filter((item) => item.jlptLevel === level).length})
                    </button>
                ))}
            </div>
            <div className={styles.kanjiMeaningGrid}>
                {filtered.map((item) => (
                    <Link
                        key={item.symbol}
                        to={`/kanji/${encodeURIComponent(item.symbol)}`}
                        className={styles.kanjiMeaningTile}
                    >
                        <span className={styles.kanjiMeaningGlyph}>{item.symbol}</span>
                        <span className={styles.kanjiMeaningText}>{item.meaning}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function ContentBlock({ block, index }) {
    if (block.heading) {
        return (
            <h2 key={index} className={styles.sectionHeading}>
                {block.heading}
            </h2>
        );
    }

    if (block.kanjiMeaningGrid) {
        return <KanjiMeaningGrid key={index} items={block.kanjiMeaningGrid} />;
    }

    if (block.list) {
        return (
            <ul key={index} className={styles.list}>
                {block.list.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        );
    }

    if (block.parts) {
        return (
            <p key={index} className={styles.paragraph}>
                {block.parts.map((part, partIndex) =>
                    typeof part === "string" ? (
                        <React.Fragment key={partIndex}>{part}</React.Fragment>
                    ) : (
                        <Link key={partIndex} to={part.to} className={styles.inlineLink}>
                            {part.label}
                        </Link>
                    )
                )}
            </p>
        );
    }

    return (
        <p key={index} className={styles.paragraph}>
            {block.text}
        </p>
    );
}

function BlogPostPage() {
    const { slug } = useParams();
    const post = getBlogPostBySlug(slug);

    if (!post) {
        return (
            <div className={styles.page}>
                <Seo title="Post not found" path={`/blog/${slug}`} noindex />
                <Header />

                <main className={styles.main}>
                    <section className={styles.container}>
                        <h1 className={styles.title}>Post not found</h1>
                        <p className={styles.description}>
                            This blog post doesn't exist or may have been moved.
                        </p>
                        <Link to="/blog" className={styles.backLink}>
                            <FontAwesomeIcon icon={faArrowLeft} /> Back to the blog
                        </Link>
                    </section>
                </main>

                <Footer />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Seo title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} />
            <Header />

            <main className={styles.main}>
                <article className={styles.container}>
                    <Link to="/blog" className={styles.backLink}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to the blog
                    </Link>

                    <header className={styles.postHeader}>
                        <div className={styles.postMeta}>
                            <time dateTime={post.date}>{formatDate(post.date)}</time>
                            {post.tags?.map((tag) => (
                                <span key={tag} className={styles.postTag}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <h1 className={styles.title}>{post.title}</h1>
                    </header>

                    <div className={styles.body}>
                        {post.content.map((block, index) => (
                            <ContentBlock key={index} block={block} index={index} />
                        ))}
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
}

export default BlogPostPage;
